<template>
  <!--
    Printed receipt (رسید) for treasury lists — paid / received cheques and
    cash payments / receipts — through the shared print module.
    Opened through the ref: open(items). One receipt per item, in the given
    order (group print = several receipts, each on its own page).
  -->
  <print-studio
    v-model="studio"
    :report="'receipt-' + kind"
    :title="title"
    :columns="columns"
    :source="source"
    :built-ins="builtIns"
    prefer-template="rcp-a5"
    :extra-ctx="baseCtx"
    :options="optionDefs"
    :batch="batch"
    :share-text="shareText"
    @error="$emit('error', $event)"
  />
</template>

<script>
import PrintStudio from "@/modules/print/PrintStudio.vue";
import { builtInReceiptTemplates } from "@/modules/print/templates";
import { numberToWords } from "@/modules/print/words";

export default {
  name: "TreasuryReceiptPrint",
  components: { PrintStudio },
  props: {
    // chequePay | chequeReceive | cashPay | cashReceive
    kind: { type: String, required: true },
  },
  data: () => ({
    studio: false,
    items: [],
  }),
  computed: {
    isCheque() {
      return this.kind.indexOf("cheque") === 0;
    },
    isPay() {
      return /Pay$/.test(this.kind);
    },
    title() {
      return this.$t("receiptTitle_" + this.kind);
    },
    builtIns() {
      return builtInReceiptTemplates;
    },
    optionDefs() {
      return [
        { key: "currency", label: this.$t("printOptCurrency"), default: true },
        { key: "amountWords", label: this.$t("receiptOptAmountWords"), default: true },
        { key: "docNumber", label: this.$t("receiptOptDocNumber"), default: true },
      ];
    },
    columns() {
      var app = this;
      var bank = function (r) {
        return r.bank_name || (r.bank && r.bank.name) || (r.cashbox && r.cashbox.name) || "";
      };
      if (this.isCheque) {
        return [
          { key: "number", label: this.$t("check-number"), width: 1.2, value: function (r) { return r.number || ""; } },
          { key: "sayad", label: this.$t("fishermanNumber"), width: 1.8, value: function (r) { return r.fisherman_number || ""; } },
          { key: "bank", label: this.$t("bank"), width: 1.6, value: bank },
          { key: "due", label: this.$t("dueDate"), width: 1.1, value: function (r) { return r.due_date || ""; } },
          { key: "amount", label: this.$t("amount"), format: "money", width: 1.5, value: function (r) { return app.num(r.amount); } },
        ];
      }
      return [
        {
          key: "title",
          label: this.$t("description"),
          width: 2.6,
          value: function (r) { return r.content || r.description || r.title || ""; },
        },
        { key: "bank", label: this.$t(this.isPay ? "receiptFrom" : "receiptTo"), width: 1.6, value: bank },
        { key: "tracking", label: this.$t("receiptTracking"), width: 1.3, value: function (r) { return r.issue_tracking || ""; } },
        { key: "amount", label: this.$t("amount"), format: "money", width: 1.5, value: function (r) { return app.num(r.amount); } },
      ];
    },
    // One receipt per item: its own parties, number/date, total, words.
    batch() {
      var app = this;
      return this.items.map(function (it) {
        return { rows: [it], ctx: app.ctxOf(it) };
      });
    },
    baseCtx() {
      return this.items.length ? this.ctxOf(this.items[0]) : {};
    },
    source() {
      var app = this;
      return {
        currentRows: function () { return app.items; },
        total: function () { return app.items.length; },
        fetchPage: function () {
          return Promise.resolve({ rows: app.items, lastPage: 1, total: app.items.length });
        },
      };
    },
    shareText() {
      if (this.items.length !== 1) return this.title;
      var it = this.items[0];
      return [this.title, this.accountName(it), this.money(it) + " " + this.currencyName(it)].filter(Boolean).join(" | ");
    },
  },
  methods: {
    open(items) {
      var list = (items || []).filter(Boolean);
      if (!list.length) return;
      this.items = list.slice();
      this.studio = true;
    },
    num(v) {
      var n = parseFloat(v);
      return isNaN(n) ? 0 : n;
    },
    currencyName(it) {
      return it.currency && it.currency.name ? it.currency.name : this.$t("rial");
    },
    decimals(it) {
      return it.currency ? Number(it.currency.decimal) || 0 : 0;
    },
    money(it) {
      var n = Number(this.num(it.amount).toFixed(this.decimals(it)));
      return this.$numberFormat ? this.$numberFormat(n) : String(n);
    },
    accountName(it) {
      var a = it.payed_account || it.account || {};
      return a.name || it.owner_name || "";
    },
    ctxOf(it) {
      var s = this.$store.state;
      var acc = it.payed_account || it.account || {};
      var us = [{ label: this.$t("name"), value: s.abrName }];
      var them = [
        { label: this.$t("name"), value: this.accountName(it) },
        { label: this.$t("national-code"), value: acc.national_code || acc.nationalCode || "" },
        { label: this.$t("mobile"), value: acc.mobile || "" },
      ];
      var payer = { title: this.$t("receiptPayer"), lines: this.isPay ? us : them };
      var receiver = { title: this.$t("receiptReceiver"), lines: this.isPay ? them : us };
      var amount = Number(this.num(it.amount).toFixed(this.decimals(it)));
      var docNo = it.doc_header && it.doc_header.number;
      return {
        title: this.title,
        currency: this.currencyName(it),
        // template "seller" box comes first: payer, then receiver
        parties: { seller: payer, buyer: receiver },
        info: [
          { label: this.$t("receiptNumber"), value: it.id },
          { label: this.$t("date"), value: it.date || it.payed_date || "" },
          { label: this.$t("receiptDocNumber"), value: docNo || "", option: "docNumber" },
        ],
        summary: [
          { label: this.$t("receiptTotal") + " (" + this.currencyName(it) + ")", value: amount, strong: true },
          { label: this.$t("chequeFieldAmountWords"), value: numberToWords(amount) + " " + this.currencyName(it), option: "amountWords", wide: true },
        ],
        note: it.content && this.isCheque ? it.content : "",
        itemsLabel: this.$t(this.isCheque ? "receiptCheques" : "receiptItems"),
        signatureLabels: [this.$t("receiptPayerSign"), this.$t("receiptReceiverSign")],
      };
    },
  },
};
</script>
